// UI wording for sign-up, forgot password and reset password (27 Oct, the sign-in build, step 6), and for the waiting
// page, change password and "your access changed" (step 7; since item 4 the account actions are on the profile page
// and the waiting page).
//
// DRAFT, FOR VANJA. Kept in its own file so nothing in strings.constant.ts changes. No rule lives here: what Firebase
// refuses, and why, comes from Firebase's error code; these are only the sentences shown for each code, and an unknown
// code shows the default one.

// ---------- Sign-up ----------

export const AUTH_COMPANY_LABEL = 'Company name';
export const AUTH_COMPANY_PLACEHOLDER = 'Your company (optional)';

// ---------- Forgot password ----------

// The same sentence whatever happened to the address (no account, a disabled one, or a sent link), so the page never
// says whether an account exists.
export const AUTH_RESET_LINK_SENT =
  "If there is an account for that address, we've sent a link to reset the password. Check your inbox and spam folder.";
export const AUTH_FORGOT_PASSWORD_LINK = 'Forgot password?';
export const AUTH_FORGOT_PASSWORD_LINK_ACTION = 'Reset it';

// ---------- The link in the email (reset password, confirm email) ----------

export const AUTH_RESET_DONE_TITLE = 'Password changed';
export const AUTH_RESET_DONE_TEXT = 'Your password has been changed. Sign in with your new password.';
export const AUTH_SIGN_IN_ACTION = 'Sign in';
export const AUTH_ASK_NEW_LINK = 'Ask for a new link';
export const AUTH_LINK_EXPIRED_PROMPT = 'Link expired?';
export const AUTH_LINK_INVALID_TITLE = 'This link no longer works';
// Item 7 (28 Oct): the view links to sign in (no new link can be asked for an undo-email link), so the words say that.
export const AUTH_LINK_INVALID_TEXT = 'The link is invalid, expired or already used. Sign in to continue.';
export const AUTH_EMAIL_CONFIRMED_TITLE = 'Email confirmed';
export const AUTH_EMAIL_CONFIRMED_TEXT = 'Thanks, your email address is confirmed. Sign in to continue.';
export const AUTH_LINK_CHECKING = 'Checking the link...';
export const AUTH_CONFIRM_EMAIL_TITLE = 'Confirm your email address';
export const AUTH_CONFIRM_EMAIL_TEXT = (email: string) =>
  `Confirm ${email} for your account. Only confirm if you created this account yourself.`;
export const AUTH_CONFIRM_EMAIL_ACTION = 'Confirm my email';
// ---------- The other email links (28 Oct, item 7): undo an email change, change the email. DRAFT (Vanja) ----------

export const AUTH_RECOVER_EMAIL_TITLE = 'Undo the email change';
export const AUTH_RECOVER_EMAIL_TEXT = (restoredEmail: string, currentEmail: string) =>
  currentEmail
    ? `This puts your account's email address back to ${restoredEmail} (it is ${currentEmail} now). Only continue if you did not ask for the change.`
    : `This puts your account's email address back to ${restoredEmail}. Only continue if you did not ask for the change.`;
export const AUTH_RECOVER_EMAIL_ACTION = 'Restore my email address';
export const AUTH_RECOVER_EMAIL_DONE_TITLE = 'Email address restored';
export const AUTH_RECOVER_EMAIL_DONE_TEXT = (restoredEmail: string) =>
  `Your email address is ${restoredEmail} again. If you didn't ask for the change, reset your password now.`;
export const AUTH_RECOVER_RESET_ACTION = 'Reset my password';
export const AUTH_RECOVER_RESET_SENT = (restoredEmail: string) => `We've sent a link to ${restoredEmail} to reset your password.`;
export const AUTH_RECOVER_RESET_FAILED = "We couldn't send the link. Use \"Forgot password?\" on the sign-in page.";
export const AUTH_CHANGE_EMAIL_TITLE = 'Change your email address';
// Never the address being replaced: this link goes to the new address (item 7's security review).
export const AUTH_CHANGE_EMAIL_TEXT = (newEmail: string) =>
  `This changes your account's email address to ${newEmail}. Only continue if you asked for this change.`;
export const AUTH_CHANGE_EMAIL_ACTION = 'Change my email address';
export const AUTH_CHANGE_EMAIL_DONE_TITLE = 'Email address changed';
export const AUTH_CHANGE_EMAIL_DONE_TEXT = (newEmail: string) => `Your email address is now ${newEmail}. Sign in with it.`;
// A failure that may pass on a retry (the network, Firebase's own fault): never "this link no longer works".
export const AUTH_LINK_CHECK_FAILED_TITLE = "We couldn't check the link";
export const AUTH_LINK_CHECK_FAILED_TEXT = 'Please check your connection and try again.';
export const AUTH_LINK_TRY_AGAIN_ACTION = 'Try again';
export const AUTH_LINK_APPLY_RETRY = "That didn't go through. Please try again.";

// ---------- Errors, by Firebase code (firebase/errorMessages.ts) ----------
// Sentences that belong to a field name that field ("email", "Password") so the form shows them beside it.

export const AUTH_ERROR_INVALID_CREDENTIALS = 'Invalid email or password. Please try again.';
export const AUTH_ERROR_INVALID_EMAIL = 'Email address is not valid.';
export const AUTH_ERROR_MISSING_EMAIL = 'Please enter your email address.';
// Neutral on purpose: Firebase still says when an address already has an account at sign-up (email enumeration
// protection does not cover account creation), so the page cannot hide it; it only avoids stating it.
export const AUTH_ERROR_EMAIL_IN_USE =
  "We couldn't create an account with this email. If you already have one, sign in or reset your password.";
// States the validator's rule (src/utils/validators.ts): 8 to 100 characters, upper and lower case, at least two
// numbers, no spaces. Change both together; the helper text under the field (PasswordHelperText) says the same.
export const AUTH_ERROR_WEAK_PASSWORD =
  'Password is too weak. Use 8 to 100 characters with upper and lower case letters, at least two numbers and no spaces.';
export const AUTH_ERROR_PASSWORD_POLICY = "Password doesn't meet the requirements. Please choose another one.";
export const AUTH_ERROR_MISSING_PASSWORD = 'Please enter a password.';
export const AUTH_ERROR_TOO_MANY = 'Too many attempts. Please wait a moment and try again.';
export const AUTH_ERROR_NETWORK = 'Network error. Check your connection and try again.';
export const AUTH_ERROR_LINK_INVALID = 'This link is invalid or expired. Ask for a new one.';
export const AUTH_ERROR_USER_DISABLED = 'This account is disabled. Contact support for help.';
export const AUTH_ERROR_SIGN_UP_CLOSED = 'New accounts cannot be created right now. Please try again later.';
export const AUTH_ERROR_GENERIC = 'Unable to complete authentication right now. Please try again.';
export const AUTH_ERROR_FIRST_NAME_LENGTH = 'First name must be 1 to 120 characters.';
export const AUTH_ERROR_LAST_NAME_LENGTH = 'Last name must be 1 to 120 characters.';
export const AUTH_ERROR_COMPANY_LENGTH = 'Company name can be at most 120 characters.';

// ---------- Form error titles ----------

export const AUTH_SIGN_UP_FAILED_TITLE = 'Sign-up failed';
export const AUTH_RESET_REQUEST_FAILED_TITLE = 'Request failed';
export const AUTH_RESET_FAILED_TITLE = 'Password not changed';

// ---------- The waiting page (step 7) ----------
// Shown to a signed-in account that has no role yet, or whose role works only inside a company and has none.

// Final wording (Joey, 28 Oct brief, item 8), not DRAFT: the four title and text strings below.
// No role yet (a new sign-up).
export const AUTH_WAITING_TITLE = 'Your account is waiting for access';
export const AUTH_WAITING_TEXT = 'Young Hollywood will review it. Please sign in again later.';
// A role, but no company (tenant).
export const AUTH_NEEDS_COMPANY_TITLE = 'Your account needs a company';
export const AUTH_NEEDS_COMPANY_TEXT = 'Please contact Young Hollywood before you continue.';
export const AUTH_WAITING_SIGNED_IN_AS = 'Signed in as';
export const AUTH_EMAIL_NOT_CONFIRMED = 'Please confirm your email address. We sent a link to your inbox.';
export const AUTH_EMAIL_CONFIRMED_SHORT = 'Your email address is confirmed.';
export const AUTH_RESEND_EMAIL = 'Resend email';
export const AUTH_RESEND_SENT = "We've sent a new link. Check your inbox and spam folder.";
export const AUTH_RESEND_WAIT = 'You can ask for another link in a minute.';
export const AUTH_I_CONFIRMED = "I've confirmed it";
export const AUTH_NOT_CONFIRMED_YET = "We can't see the confirmation yet. Open the link in the email, then try again.";
export const AUTH_SAVE_DETAILS_TEXT = "Your name and company didn't reach us when you signed up.";
export const AUTH_SAVE_DETAILS = 'Save my details';
export const AUTH_SAVE_DETAILS_DONE = 'Your details are saved.';
export const AUTH_SAVE_DETAILS_FAILED = "Your details couldn't be saved. Please try again.";
export const AUTH_SIGN_OUT = 'Sign out';

// ---------- The waiting homepage (29 Oct): a confirmed account with no role, at / ----------
// Its title is AUTH_WAITING_TITLE above (final). DRAFT, FOR VANJA: the line under it, the name of a trending picture
// with no title (read by a screen reader), and the confirm-email page's way to the homepage when signed in.
export const AUTH_WAITING_HOME_TEXT = "Young Hollywood will review your account. Until then, here is a look at what's on The Code Word.";
export const AUTH_WAITING_PREVIEW_UNTITLED = 'Trending video';
// Never "your": the account signed in here may not be the one whose address the link confirmed.
export const AUTH_EMAIL_CONFIRMED_SIGNED_IN_TEXT = 'This email address is confirmed.';
export const AUTH_GO_TO_HOMEPAGE = 'Go to the homepage';

// ---------- Change password and Confirm my email (step 7b; on the profile page and the waiting page since item 4) ------

export const AUTH_CHANGE_PASSWORD = 'Change password';
export const AUTH_CHANGE_PASSWORD_TITLE = 'Check your email';
// Joey's wording (28 Oct brief, item 4).
export const AUTH_CHANGE_PASSWORD_SENT = (email: string) =>
  `We've sent a link to ${email} to change your password. You've been signed out.`;
export const AUTH_CHANGE_PASSWORD_OK = 'OK';
export const AUTH_CHANGE_PASSWORD_FAILED = "We couldn't send the link. Please try again later.";
export const AUTH_CHANGE_PASSWORD_FAILED_TITLE = 'Link not sent';
// DRAFT (Vanja): the profile page's Confirm my email (28 Oct, item 4), shown only while the address is unconfirmed.
export const AUTH_CONFIRM_MY_EMAIL = 'Confirm my email';
export const AUTH_CONFIRM_MY_EMAIL_TITLE = 'Check your email';
export const AUTH_CONFIRM_MY_EMAIL_SENT = (email: string) =>
  `We've sent a confirmation link to ${email}. Open it to confirm your address.`;
export const AUTH_CONFIRM_MY_EMAIL_FAILED = "We couldn't send the link. Please try again later.";

// ---------- Signed out because access changed (step 7) ----------

export const AUTH_ACCESS_CHANGED = 'Your access has changed. Please sign in again.';
export const AUTH_SIGNOUT_INCOMPLETE =
  "We couldn't finish signing you out everywhere. If this is a shared computer, close the browser.";
export const AUTH_WAITING_LOAD_FAILED = "This page didn't load. Please reload it.";
export const AUTH_RELOAD = 'Reload';

// ---------- The gateway could not check the sign-in, or too many requests (28 Oct, items 1 and 2) ----------

export const AUTH_CHECK_UNAVAILABLE = "We couldn't check your sign-in just now. Please try again in a moment.";
export const AUTH_RATE_LIMITED = 'Too many attempts. Please wait a moment and try again.';
export const AUTH_NOTICE_CLOSE = 'Close';
